import { ContentCopy, ErrorOutlined, Refresh } from "@mui/icons-material";
import { motion } from "motion/react";
import { FC } from "react";

import { MuiButton } from "../../controls/forms/buttons/button";
import { CollapsableContainer } from "../collapse/collapsable-container";
import { CollapseIcon } from "../collapse/collapse-icon";
import { FramerTooltip } from "../tooltips/framer-tooltip";
import {
  ErrorBoundaryDetailsPanelStyled,
  ErrorBoundaryFallbackStyled,
} from "./error-boundary-fallback.styled";
import { ErrorBoundaryLanguageSwitcher } from "./error-boundary-language-switcher";
import type { IErrorBoundaryFallbackProps } from "./error-boundary.models";
import { useErrorBoundaryFallback } from "./use-error-boundary-fallback";

export const ErrorBoundaryFallback: FC<IErrorBoundaryFallbackProps> = ({
  error,
  errorInfo,
}) => {
  const {
    fullReport,
    texts,
    locale,
    isRtl,
    isDetailsExpanded,
    handleLocaleChange,
    handleToggleDetails,
    handleCopyReport,
    handleReloadPage,
  } = useErrorBoundaryFallback({ error, errorInfo });

  const expandLabel = isDetailsExpanded
    ? texts.closeDetails
    : texts.openDetails;

  return (
    <ErrorBoundaryFallbackStyled
      role="alert"
      aria-live="assertive"
      lang={locale}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <motion.div
        className="error-boundary__card"
        initial={{ opacity: 0, y: 24, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        <ErrorBoundaryLanguageSwitcher
          locale={locale}
          texts={texts}
          onLocaleChange={handleLocaleChange}
        />
        <div className="error-boundary__icon-wrap">
          <ErrorOutlined className="error-boundary__icon" aria-hidden />
        </div>
        <p className="error-boundary__brand">RYDE</p>
        <h1 className="error-boundary__title">{texts.title}</h1>
        <p className="error-boundary__subtitle">{texts.subtitle}</p>

        {error.message ? (
          <ErrorBoundaryDetailsPanelStyled>
            <div className="error-boundary-details__bar">
              <p className="error-boundary-details__preview">{error.message}</p>
              <div className="error-boundary-details__actions">
                <FramerTooltip content={expandLabel}>
                  <button
                    type="button"
                    className="error-boundary-details__button"
                    aria-expanded={isDetailsExpanded}
                    aria-label={expandLabel}
                    onClick={handleToggleDetails}
                  >
                    <CollapseIcon
                      isCollapsed={isDetailsExpanded}
                      variant="quaternary"
                    />
                  </button>
                </FramerTooltip>
                <button
                  type="button"
                  className="error-boundary-details__button"
                  aria-label={texts.copyDetails}
                  onClick={handleCopyReport}
                >
                  <ContentCopy />
                </button>
              </div>
            </div>
            <CollapsableContainer isCollapsed={isDetailsExpanded}>
              <pre className="error-boundary-details__full">{fullReport}</pre>
            </CollapsableContainer>
          </ErrorBoundaryDetailsPanelStyled>
        ) : null}

        <div className="error-boundary__actions">
          <MuiButton
            muiStyle="primary"
            startIcon={<Refresh />}
            onClick={handleReloadPage}
          >
            {texts.reloadPage}
          </MuiButton>
        </div>
      </motion.div>
    </ErrorBoundaryFallbackStyled>
  );
};
